<template>
  <v-card rounded="lg" variant="outlined" class="pa-6">
    <v-card-title class="text-h5 px-0">Make a donation</v-card-title>
    <p class="mb-5">Choose where your gift will make a difference.</p>
    <v-progress-linear v-if="loadingItems" indeterminate />
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{
      error
    }}</v-alert>
    <p v-if="initiative?.targetFund" class="mb-4">
      {{ initiative.initiativeName }} supports {{ fund.fundName }}.
    </p>
    <v-select
      v-else
      :model-value="fund.fundId"
      label="Choose a fund"
      :items="options.funds.filter((fund) => fund.showInCheckout)"
      item-title="fundName"
      item-value="fundId"
      @update:model-value="emit('fundChanged', $event)"
    />
    <v-select
      v-if="items.length > 1"
      v-model="selectedId"
      label="Donation item"
      :items="items"
      item-title="itemName"
      item-value="instanceId"
    />
    <v-text-field
      v-model="inMemoryOf"
      label="In honor or memory of (optional)"
      maxlength="500"
    />
    <v-radio-group
      v-if="selected?.allowPriceOverride"
      v-model="amount"
      label="Choose a donation amount"
      class="donation-amount-options"
    >
      <v-radio
        v-for="cents in [100000, 50000, 25000, 10000, 5000, 2500]"
        :key="cents"
        :label="formatRawCost(cents)"
        :value="cents"
      />
    </v-radio-group>
    <CurrencyInput
      v-if="selected?.allowPriceOverride"
      v-model:amount-cents="amount"
      label="Or enter a custom amount"
      required
    />
    <p v-else-if="selected" class="my-4">
      {{ formatRawCost(selected.priceCents ?? 0) }}
    </p>
    <div class="d-flex ga-2 mt-4">
      <v-btn
        variant="outlined"
        class="flex-1-1"
        :loading="loading && !checkout"
        :disabled="!selected || !session?.canEdit.value || loading"
        @click="submit(false)"
      >
        Add to cart
      </v-btn>
      <v-btn
        color="primary"
        class="flex-1-1"
        :loading="loading && checkout"
        :disabled="!selected || !session?.canEdit.value || loading"
        @click="submit(true)"
      >
        Checkout now
      </v-btn>
    </div>
    <v-btn
      v-if="
        session?.orderRef.value && !session.ready.value && !session.busy.value
      "
      class="mt-4"
      @click="reloadCart"
      >Reload cart</v-btn
    >
  </v-card>
</template>
<script lang="ts" setup>
import { useCatalog } from "@/composables/shop/useCatalog";
import type { ProductListing } from "@/api/models/shop/ProductListing";
import type { ConfigureDelivery } from "../../../cbi-generator/generated/dto/ConfigureDelivery";
import { PurchaseDeliveryMethod } from "../../../cbi-generator/generated/dto/PurchaseDeliveryMethod";
import { PurchaseDeliveryTarget } from "../../../cbi-generator/generated/dto/PurchaseDeliveryTarget";
import type {
  DonationFund,
  DonationInitiative,
  DonationOptions,
} from "@/composables/donations/useDonationNew";
import { buildMemberPurchase } from "@/composables/shop/memberPurchase";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useUtility } from "@/composables/useUtility";
import { errorMessage } from "@/composables/generated/contracts";

const props = defineProps<{
  fund: DonationFund;
  initiative?: DonationInitiative;
  options: DonationOptions;
}>();
const emit = defineEmits<{
  fundChanged: [id: number];
  amountChanged: [amount: number];
}>();
const session = inject<MemberOrderSession | null>(memberOrderSessionKey, null);
const router = useRouter();
const { formatRawCost } = useUtility();
const amount = ref(0);
const inMemoryOf = ref("");
const checkout = ref(false);
const items = ref<ProductListing[]>([]);
const selectedId = ref<number>();
const selected = computed(() =>
  items.value.find((item) => item.instanceId === selectedId.value),
);
const loading = ref(false);
const loadingItems = ref(true);
const error = ref("");
watch([amount, selected], () =>
  emit(
    "amountChanged",
    selected.value?.allowPriceOverride
      ? amount.value
      : (selected.value?.priceCents ?? 0),
  ),
);
onMounted(async () => {
  try {
    items.value = (await useCatalog()).products.filter((item) =>
      item.isDonationProduct(),
    );
    if (!items.value.length)
      throw new Error("Donations are not currently available for purchase.");
    selectedId.value = items.value[0].instanceId;
  } catch (cause) {
    error.value = errorMessage(cause);
  } finally {
    loadingItems.value = false;
  }
});
async function reloadCart() {
  try {
    await session?.refresh();
  } catch (cause) {
    error.value = errorMessage(cause);
  }
}
function validatePurchase() {
  if (!selected.value) throw new Error("Choose an available donation item.");
  buildMemberPurchase(selected.value, amount.value);
  if (inMemoryOf.value.length > 500)
    throw new Error("The dedication cannot exceed 500 characters.");
}
// Donations are always acknowledged to the purchaser; recipient lookup is disabled for now.
const purchaserDelivery: ConfigureDelivery = {
  target: PurchaseDeliveryTarget.PURCHASER,
  method: PurchaseDeliveryMethod.EMAIL,
  personLookup: null,
  targetPerson: null,
  targetAddress: null,
};
async function submit(goToCheckout: boolean) {
  if (!session || !selected.value || loading.value) return;
  checkout.value = goToCheckout;
  loading.value = true;
  error.value = "";
  try {
    validatePurchase();
    const request = buildMemberPurchase(selected.value, amount.value, {
      fund: { fundId: props.fund.fundId },
      initiative: props.initiative
        ? { initiativeId: props.initiative.initiativeId }
        : props.options.defaultInitiative,
      inMemoryOf: inMemoryOf.value.trim() || null,
      restriction: null,
      description: null,
      isMatch: false,
      inKind: false,
      skipAck: false,
    });
    await session.addPurchase(request, purchaserDelivery);
    await router.push({
      name: checkout.value ? "portal-checkout" : "portal-cart",
    });
  } catch (cause) {
    error.value = errorMessage(cause);
  } finally {
    loading.value = false;
  }
}
</script>
<style scoped>
.donation-amount-options :deep(.v-selection-control-group) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 16px;
}
/* Vuetify assigns grid-area: control to selection controls, stacking them all in one cell. */
.donation-amount-options :deep(.v-selection-control-group > .v-radio) {
  grid-area: auto;
}
</style>
